import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Lazy initialization of Supabase client
let supabaseInstance: SupabaseClient | null = null;

const getSupabaseClient = (): SupabaseClient => {
  if (supabaseInstance) {
    return supabaseInstance;
  }

  // Find the correct URL and key values
  let supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY env variables');
  }

  if (import.meta.env.DEV) {
    const isLocalSupabase =
      supabaseUrl.includes('127.0.0.1:54321') ||
      supabaseUrl.includes('localhost:54321');
    if (isLocalSupabase) {
      supabaseUrl = 'http://localhost:3001/supabase';
    }
  }


  if (import.meta.env.DEV) {
    console.info('[Supabase] URL:', supabaseUrl);
    console.info('[Supabase] AnonKey head:', supabaseAnonKey?.slice(0, 8) + '…');
  }

  // Create the Supabase client with explicitly configured auth settings
  supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: localStorage,
      flowType: 'implicit' // Use implicit flow for SPA
    },
    db: {
      schema: 'public'
    },
    global: {
      headers: {
        'Accept': 'application/json'
      }
    }
  });

  return supabaseInstance;
};

// Export a function that gets the client when needed
export const supabase = new Proxy({} as SupabaseClient, {
  get(target, prop) {
    const client = getSupabaseClient();
    return client[prop as keyof SupabaseClient];
  }
});

// Add a debug function to help check authentication status
export const checkAuthStatus = async () => {
  try {
    const client = getSupabaseClient();
    const session = await client.auth.getSession();
    console.log("Current session status:", session);

    if (session?.data?.session) {
      const expiresAt = session.data.session.expires_at;
      const expiresIn = expiresAt ? `${(expiresAt * 1000 - Date.now()) / 60000} minutes` : 'unknown';
      console.log(`Session expires in: ${expiresIn}`);

    }

    return session;
  } catch (error) {
    console.error("Error checking auth status:", error);
    return null;
  }
};
